import React from 'react';
import styled from 'styled-components';

import Checkbox from 'components/shared/form/Checkbox';
import DatePicker from 'components/shared/form/DatePicker';
import FormSection from 'components/shared/form/FormSection';
import { MultiSelectChips } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { ClientGroupTypeConfig } from 'models/flexible/ClientGroupType';
import { adminIndexClassifications } from 'services/api/admin/classifications';
import { colors } from 'styles/theme/colors';

type Classification = { id: string; name: string };

type Props = {
  config: ClientGroupTypeConfig;
  onChange: (config: ClientGroupTypeConfig) => void;
  disabled: boolean;
  clientId: string;
  groupTypeName?: string;
};

const parseDate = (dateStr: string | undefined): Date | null => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatDate = (date: Date | null): string | undefined => {
  if (!date) return undefined;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export default function ConfigureCareOptionsForm({ config, onChange, disabled, clientId, groupTypeName }: Props) {
  const umEnabled = config.utilizationManagement.enabled;

  const planTypeOptions = useInfiniteOptions<Classification>({
    queryKey: ['adminClassifications', 'plan_type', clientId],
    fetchFn: adminIndexClassifications,
    params: { type: 'plan_type', client: clientId, sortBy: 'name asc' },
  });

  const set = <K extends keyof ClientGroupTypeConfig>(key: K, value: ClientGroupTypeConfig[K]) =>
    onChange({ ...config, [key]: value });

  const handleUmEnabledChange = (enabled: boolean) => {
    onChange({
      ...config,
      utilizationManagement: {
        ...config.utilizationManagement,
        enabled,
        ...(enabled ? {} : { planTypeClassification: [], aiSummary: false }),
      },
    });
  };

  const launchDateLabel = groupTypeName ? `${groupTypeName} Launch Date` : 'Launch Date';

  return (
    <Container>
      <Card>
        <SectionHeader>Launch Date</SectionHeader>
        <InputGroup title={launchDateLabel}>
          <DatePicker
            selected={parseDate(config.launchDate)}
            onChange={(date) => set('launchDate', formatDate(date))}
            disabled={disabled}
            clearable={false}
            minDate={new Date()}
          />
        </InputGroup>
      </Card>

      <Card>
        <SectionHeader>Enabled Features</SectionHeader>
        <StyledFormSection>
          <StyledCheckbox
            checked={config.utilizationManager}
            onChange={(val) => set('utilizationManager', val)}
            disabled={disabled}
            label='Utilization Manager Field'
            labelSize='14px'
            checkedColor={colors.blue100}
            size={14}
            dataCyLabel='utilizationManagerFieldConfigureCareOptionsCheckbox'
          />

          <StyledCheckbox
            checked={config.utilizationManagement.enabled}
            onChange={(val) => {
              handleUmEnabledChange(val);
            }}
            disabled={disabled}
            label='UM Authorizations'
            labelSize='14px'
            checkedColor={colors.blue100}
            size={14}
            dataCyLabel='umAuthorizationsConfigureCareOptionsCheckbox'
          />

          <SubGroup>
            <AISummaryOpacityContainer $enabled={umEnabled}>
              <StyledCheckbox
                checked={config.utilizationManagement.aiSummary}
                onChange={(val) =>
                  onChange({
                    ...config,
                    utilizationManagement: { ...config.utilizationManagement, aiSummary: val },
                  })
                }
                disabled={disabled || !umEnabled}
                label='AI Summary for Concurrent Review'
                labelSize='14px'
                checkedColor={colors.blue100}
                size={14}
                dataCyLabel='umAuthorizationsAISummaryCheckbox'
              />
            </AISummaryOpacityContainer>

            <InputGroup title='UM Enabled Plan Types'>
              <MultiSelectChips
                {...planTypeOptions}
                values={config.utilizationManagement.planTypeClassification}
                onChange={(vals) =>
                  onChange({
                    ...config,
                    utilizationManagement: { ...config.utilizationManagement, planTypeClassification: vals },
                  })
                }
                getOptionValue={(o) => o!.id}
                getOptionLabel={(o) => o!.name}
                placeholder='All Plan Types'
                disabled={disabled || !umEnabled}
              />
            </InputGroup>
          </SubGroup>

          <HR />

          <StyledCheckbox
            checked={config.caseManager}
            onChange={(val) => set('caseManager', val)}
            disabled={disabled}
            label='Case Manager Field'
            labelSize='14px'
            checkedColor={colors.blue100}
            size={14}
            dataCyLabel='caseManagerFieldConfigureCareOptionsCheckbox'
          />

          <StyledCheckbox
            checked={config.projectedDischarge.enabled}
            onChange={(val) => onChange({ ...config, projectedDischarge: { enabled: val } })}
            disabled={disabled}
            label='Discharge Planning'
            labelSize='14px'
            checkedColor={colors.blue100}
            size={14}
            dataCyLabel='pddConfigureCareOptionsCheckbox'
          />

          <HR />

          <StyledCheckbox
            checked={config.referral}
            onChange={(val) => set('referral', val)}
            disabled={disabled}
            label='Referral'
            labelSize='14px'
            checkedColor={colors.blue100}
            size={14}
            dataCyLabel='referralsConfigureCareOptionsCheckbox'
          />

          <StyledCheckbox
            checked={config.safeReturn}
            onChange={(val) => set('safeReturn', val)}
            disabled={disabled}
            label='Safe Return'
            labelSize='14px'
            checkedColor={colors.blue100}
            size={14}
            dataCyLabel='safeReturnConfigureCareOptionsCheckbox'
          />
        </StyledFormSection>
      </Card>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Card = styled.div`
  border-radius: 5px;
  border: 1px solid ${({ theme }) => theme.colors.black10};
  background-color: ${({ theme }) => theme.colors.white};
  padding: 24px;
  width: 500px;
`;

const StyledFormSection = styled(FormSection)`
  display: flex;
  flex-direction: column;
`;

const StyledCheckbox = styled(Checkbox)`
  margin-top: 16px;

  &:first-child {
    margin-top: 8px;
  }

  &:not(:last-child) {
    margin-bottom: 16px;
  }
`;

const SubGroup = styled.div`
  padding-left: 24px;
`;

const AISummaryOpacityContainer = styled.div<{ $enabled: boolean }>`
  opacity: ${({ $enabled }) => ($enabled ? 1 : 0.5)};
  margin-bottom: 16px;
`;

const HR = styled.hr`
  border: none;
  height: 1px;
  background-color: ${colors.black10};
  margin: 16px 0;
`;
